import type { Scenario, Unit, UnitStatus } from './scenario';

// Source: VA Einsatz der Einsatzleitung Rettungsdienst, Bereich 212, version 1.0.
const DOCTOR_TYPES = ['nef', 'naw', 'itw', 'rth'];
const COUNTED_STATUSES: UnitStatus[] = ['alerted', 'onScene'];

function isCounted(unit: Unit, types: readonly string[]): boolean {
  return types.includes(unit.type) && COUNTED_STATUSES.includes(unit.status);
}

export type ElrdRecommendation = 'orgl' | 'orgl+lna';
export type ElrdFindingLevel = 'warning' | 'info';
export type ElrdFinding = { level: ElrdFindingLevel; message: string };

export type ElrdAlarm = {
  injured: number;
  doctors: number;
  reasonMet: boolean;
  recommendation: ElrdRecommendation;
  because: string;
  findings: ElrdFinding[];
};

// Pure derivation of the VA Einsatz der Einsatzleitung Rettungsdienst decision, plus a
// comparison against what the scenario's units actually show. Never blocks anything; the
// result is shown to the instructor as guidance only.
export function elrdAlarm(scenario: Scenario): ElrdAlarm {
  const injured = scenario.patients.length;
  const doctors = scenario.units.filter((u) => isCounted(u, DOCTOR_TYPES)).length;
  const reasonMet = injured > 4;
  const disaster = scenario.disaster ?? false;

  let recommendation: ElrdRecommendation;
  let because: string;
  // Checked in the order of the VA: disaster, then 3 or more doctors, then 10 or more injured.
  if (disaster) {
    recommendation = 'orgl+lna';
    because = 'Katastrophenlage';
  } else if (doctors >= 3) {
    recommendation = 'orgl+lna';
    because = '3 oder mehr NAs im Einsatz';
  } else if (injured >= 10) {
    recommendation = 'orgl+lna';
    because = '10 oder mehr Verletzte';
  } else {
    recommendation = 'orgl';
    because = 'keines der Kriterien für OrgL + LNA erfüllt';
  }

  const orglAlerted = scenario.units.some((u) => isCounted(u, ['orgl']));
  const lnaAlerted = scenario.units.some((u) => isCounted(u, ['lna']));

  const findings: ElrdFinding[] = [];
  if (recommendation === 'orgl+lna' && !lnaAlerted) {
    findings.push({
      level: 'warning',
      message: `Nach VA sind OrgL und LNA zu alarmieren (${because}), im Szenario ist kein LNA alarmiert.`,
    });
  }
  if (!orglAlerted && (reasonMet || disaster || recommendation === 'orgl+lna')) {
    findings.push({
      level: 'warning',
      message: 'Nach VA ist die EL-RD zu alarmieren, im Szenario ist kein OrgL alarmiert.',
    });
  }
  if (recommendation === 'orgl' && lnaAlerted) {
    findings.push({
      level: 'info',
      message:
        'Nach VA genügt der OrgL allein; der LNA ist zusätzlich alarmiert (Nachalarmierung ist erlaubt).',
    });
  }

  return { injured, doctors, reasonMet, recommendation, because, findings };
}
